import { Store } from "./store";

export interface MemoizedValue {
    displayValue: string;
    isStale: boolean;
    isReaded: boolean;
}

export class Memoizer {
    public readonly memoizedData = new Store<MemoizedValue>();
    public lastUpdate: Date | undefined;
    private defaultValues: Record<string, string> = {};
    private readonly watchKeysList: string[] = [];
    private onReady: (() => void)[] = [];

    constructor(private readonly data: Store<string>) {
        this.data.subscribe(() => {
            const changes: Record<string, MemoizedValue> = {};
            for (const key of this.watchKeysList) {
                this.updateKey(key, changes);
            }
            this.memoizedData.setMany(changes);
            if (this.onReady.length) {
                let isAllReaded = true;
                for (const key of this.watchKeysList) {
                    if (!this.memoizedData.get(key)?.isReaded) {
                        isAllReaded = false;
                        break;
                    }
                }
                if (isAllReaded) {
                    for (const callback of this.onReady) {
                        callback();
                    }
                    this.onReady = [];
                }
            }
        });
    }

    public addOnReady(callback: () => void): void {
        this.onReady.push(callback);
    }

    public setDefaultValues(readings: Record<string, string>): void {
        this.defaultValues = { ...this.defaultValues, ...readings };
    }

    public setLastUpdate(date: Date): void {
        this.lastUpdate = date;
    }

    public addKeysToWatch(keys: string[]): void {
        const changes: Record<string, MemoizedValue> = {};
        for (const key of keys) {
            if (!this.watchKeysList.includes(key)) {
                this.watchKeysList.push(key);
                this.updateKey(key, changes);
            }
        }
        this.memoizedData.setMany(changes);
    }

    private updateKey(key: string, changes: Record<string, MemoizedValue>): void {
        const currentValue = this.data.get(key);
        let previous = this.memoizedData.get(key);
        if (!previous) {
            previous = {
                displayValue: "",
                isStale: true,
                isReaded: false,
            };
        }
        if (previous.displayValue === currentValue && !previous.isStale) {
            if (currentValue !== "" && currentValue !== undefined && currentValue !== "NORT") {
                this.lastUpdate = new Date();
            }
            return;
        }
        if (currentValue === "" || currentValue === undefined || currentValue === "NORT") {
            if (!previous.displayValue) {
                previous.displayValue = this.defaultValues[key] || "";
            }

            changes[key] = {
                displayValue: previous.displayValue,
                isStale: true,
                isReaded: currentValue !== undefined,
            };
            return;
        }
        changes[key] = {
            displayValue: currentValue,
            isStale: false,
            isReaded: true,
        };
        this.lastUpdate = new Date();
    }
}
